/**
 * HALDEN — small UI behaviours (no frameworks, no animation libraries).
 *
 *   [data-fade]          fades up once when it scrolls into view
 *   <menu-drawer>        the mobile menu (a <dialog>)
 *   <rotating-bar>       announcement messages, one at a time, with arrows
 *   <count-down>         counts down to a real sale end, or today's dispatch cut-off
 *   <delivery-date>      "Get it by Tue, 3 Oct" from shipping days (cut-off, weekends)
 *   <before-after>       drag slider between two photos
 *   <slider-arrows>      prev/next buttons for a horizontal slider
 *   <predictive-search>  results while typing (header search sheet)
 *   <recently-viewed>    products this visitor opened before (this browser only)
 *   [data-dialog-open]   opens a <dialog> sheet (filters, quick add, search)
 */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- fade-in */
  const fadeIo =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) =>
            entries.forEach((e) => {
              if (!e.isIntersecting) return;
              e.target.classList.add('is-in');
              e.target.dispatchEvent(new CustomEvent('fade:in', { bubbles: true }));
              fadeIo.unobserve(e.target);
            }),
          { rootMargin: '0px 0px -8% 0px' }
        )
      : null;
  const watchFades = (root = document) => {
    root.querySelectorAll('[data-fade]:not(.is-in)').forEach((el) => (fadeIo ? fadeIo.observe(el) : el.classList.add('is-in')));
  };
  /* numbers count up when their fade-in starts */
  const countUp = (root) => {
    root.querySelectorAll('[data-count]').forEach((el) => {
      const end = Number(el.dataset.count) || 0;
      if (reduced || !end) return;
      const t0 = performance.now();
      const step = (t) => {
        const k = Math.min(1, (t - t0) / 1400);
        el.textContent = Math.round(end * (1 - Math.pow(1 - k, 3)));
        if (k < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  };
  document.addEventListener('fade:in', (e) => countUp(e.target));
  document.addEventListener('DOMContentLoaded', () => watchFades());
  document.addEventListener('shopify:section:load', (e) => watchFades(e.target));

  /* ------------------------------------------------ sheets and dialogs */
  /* [data-dialog-open="ID"] opens <dialog id="ID">; [data-dialog-close] or a
     tap on the backdrop closes it. Used by filters, quick add and search. */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.dialogOpen);
      if (dialog) e.preventDefault(); // links (search icon) still work without JS
      if (dialog && !dialog.open) {
        dialog.showModal();
        dialog.dispatchEvent(new CustomEvent('dialog:open'));
      }
      return;
    }
    if (e.target.closest('[data-dialog-close]')) e.target.closest('dialog')?.close();
    else if (e.target.matches('dialog.sheet')) e.target.close();
  });

  /* ------------------------------------------------------------- motion */
  /* One quiet motion language: transforms, opacity and clip-path only,
     started by IntersectionObserver, off with reduced motion or when
     Theme settings → Layout → Animations is off (html.no-fade). */
  const motionOn = !reduced && !document.documentElement.classList.contains('no-fade') && 'IntersectionObserver' in window;
  const reveal = (sel, root, cls, margin = '0px 0px -12% 0px') => {
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          e.target.classList.add(cls);
          io.unobserve(e.target);
        }),
      { rootMargin: margin }
    );
    root.querySelectorAll(sel).forEach((el) => io.observe(el));
  };

  /* headlines rise line by line: words are grouped by the line they sit on */
  function splitLines(h) {
    if (h.dataset.split) return;
    h.dataset.split = '1';
    const original = h.innerHTML;
    const words = [];
    const walk = (node, wrapIn) => {
      [...node.childNodes].forEach((n) => {
        if (n.nodeType === 3) {
          n.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) return words.push(document.createTextNode(' '));
            let w = document.createElement('span');
            w.className = 'word';
            w.textContent = part;
            if (wrapIn) {
              const clone = wrapIn.cloneNode(false);
              clone.appendChild(w);
              w = clone;
            }
            words.push(w);
          });
        } else if (n.nodeType === 1) walk(n, n.tagName === 'BR' ? null : n);
      });
    };
    walk(h, null);
    h.textContent = '';
    words.forEach((w) => h.appendChild(w));
    const lines = [];
    let top = null;
    words.forEach((w) => {
      if (w.nodeType === 3) return lines.length && lines[lines.length - 1].push(w);
      const t = w.offsetTop;
      if (top === null || Math.abs(t - top) > 4) {
        lines.push([]);
        top = t;
      }
      lines[lines.length - 1].push(w);
    });
    h.textContent = '';
    lines.forEach((line, i) => {
      const outer = document.createElement('span');
      outer.className = 'line';
      const inner = document.createElement('span');
      inner.style.setProperty('--line', i);
      line.forEach((w) => inner.appendChild(w));
      outer.appendChild(inner);
      h.appendChild(outer);
      h.appendChild(document.createTextNode(' '));
    });
    /* after the entrance, restore the original markup so it reflows freely */
    h.addEventListener('lines:done', () => (h.innerHTML = original), { once: true });
    h.dataset.lines = lines.length;
  }

  function initMotion(root = document) {
    if (!motionOn) return;
    /* photos wipe open (never the first-screen hero or product gallery) */
    root.querySelectorAll('.frame:not(.hero__bg):not(.gallery):not(.gallery__slide):not(.hx__photo):not(.pin__media), .step__media, .review-card__img, .rw__img').forEach((el) => el.classList.add('wipe'));
    reveal('.wipe', root, 'is-shown');
    /* section headlines rise line by line */
    const heads = [...root.querySelectorAll('.section-head h2, .image-text__copy h2, .cta h2')];
    const hio = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const h = e.target;
          hio.unobserve(h);
          h.classList.add('lines-in');
          setTimeout(() => h.dispatchEvent(new CustomEvent('lines:done')), 1000 + Number(h.dataset.lines || 1) * 90);
        }),
      { rootMargin: '0px 0px -10% 0px' }
    );
    /* split once the fonts are in, so line breaks are the real ones */
    (document.fonts?.ready || Promise.resolve()).then(() =>
      heads.forEach((h) => {
        const box = h.getBoundingClientRect();
        if (box.top < window.innerHeight * 0.9 && box.bottom > 0) return; // already on screen: leave it
        h.classList.add('lines');
        splitLines(h);
        hio.observe(h);
      })
    );
  }
  document.addEventListener('DOMContentLoaded', () => initMotion());
  document.addEventListener('shopify:section:load', (e) => initMotion(e.target));

  /* hero parallax: a gentle drift of the photo while the hero is on screen */
  document.addEventListener('DOMContentLoaded', () => {
    const img = document.querySelector('.hero[data-parallax] .hero__bg img');
    if (!motionOn || !img) return;
    const hero = img.closest('.hero');
    const strength = window.matchMedia('(min-width: 750px)').matches ? 0.16 : 0.06;
    let visible = true;
    let ticking = false;
    new IntersectionObserver(([e]) => (visible = e.isIntersecting)).observe(hero);
    hero.classList.add('is-parallax');
    const update = () => {
      ticking = false;
      if (!visible) return;
      const y = Math.min(window.scrollY * strength, img.offsetHeight * 0.045);
      img.style.transform = `translate3d(0, ${Math.round(y)}px, 0) scale(1.1)`;
    };
    window.addEventListener('scroll', () => !ticking && (ticking = true) && requestAnimationFrame(update), { passive: true });
    update();
  });

  /* 3D ornaments: each field gets its scroll progress (--sy) and the cursor
     position (--mx/--my); CSS turns them into depth. Tapping a shape spins it. */
  const initOrnaments = (root = document) => {
    const fields = [...root.querySelectorAll('[data-orn-field]')];
    if (!fields.length) return;
    fields.forEach((f) =>
      f.addEventListener('click', (e) => {
        const img = e.target.closest('.orn img');
        if (!img) return;
        img.classList.remove('is-spin');
        void img.offsetWidth;
        img.classList.add('is-spin');
        img.addEventListener('animationend', () => img.classList.remove('is-spin'), { once: true });
      })
    );
    if (reduced || document.body.classList.contains('orn-still')) return;
    const awake = new Set();
    const io = new IntersectionObserver((es) => es.forEach((e) => (e.isIntersecting ? awake.add(e.target) : awake.delete(e.target))), { rootMargin: '100px 0px' });
    fields.forEach((f) => io.observe(f));
    let ticking = false;
    const paint = () => {
      ticking = false;
      const vh = window.innerHeight;
      awake.forEach((f) => {
        const r = f.getBoundingClientRect();
        f.style.setProperty('--sy', ((r.top + r.height / 2 - vh / 2) / vh * -1).toFixed(3));
      });
    };
    const ask = () => !ticking && (ticking = true) && requestAnimationFrame(paint);
    window.addEventListener('scroll', ask, { passive: true });
    if (window.matchMedia('(hover: hover)').matches) {
      let raf = 0;
      window.addEventListener('pointermove', (e) => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const mx = ((e.clientX / window.innerWidth) * 2 - 1).toFixed(3);
          const my = ((e.clientY / window.innerHeight) * 2 - 1).toFixed(3);
          awake.forEach((f) => {
            f.style.setProperty('--mx', mx);
            f.style.setProperty('--my', my);
          });
        });
      }, { passive: true });
    }
    paint();
  };
  document.addEventListener('DOMContentLoaded', () => initOrnaments());
  document.addEventListener('shopify:section:load', (e) => initOrnaments(e.target));

  /* ------------------------------------------ floating buttons step aside */
  /* The Support pill and the gift button never cover a call to action: while
     one passes through the bottom strip of the screen, <html data-cta-zone>
     is set and both fade out (CSS in critical.css). */
  document.addEventListener('DOMContentLoaded', () => {
    if (!('IntersectionObserver' in window)) return;
    const busy = new Set();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => (e.isIntersecting ? busy.add(e.target) : busy.delete(e.target)));
        document.documentElement.toggleAttribute('data-cta-zone', busy.size > 0);
      },
      { rootMargin: `-${Math.max(0, window.innerHeight - 130)}px 0px 0px 0px` }
    );
    document.querySelectorAll('.pb-buy, .hero__buttons, .hx__cta, .hx__trust, .hb__actions, .guar__seal, .ctab__actions, .how__cta, .pb-fbt__foot, .cta .button, .pin__cta, .steps__cta').forEach((el) => io.observe(el));
  });

  /* ------------------------------------------------------------ menu drawer */
  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      const dialog = this.querySelector('dialog');
      this.querySelector('[data-open]')?.addEventListener('click', () => dialog.showModal());
      this.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => dialog.close()));
      dialog.addEventListener('click', (e) => e.target === dialog && dialog.close());
    }
  }
  customElements.define('menu-drawer', MenuDrawer);

  /* -------------------------------------------------------- rotating bar */
  class RotatingBar extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-item]')];
      if (this.items.length < 2) return;
      this.i = 0;
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(-1, true));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(1, true));
      if (!reduced) this.timer = setInterval(() => this.go(1), (Number(this.dataset.interval) || 5) * 1000);
    }
    go(step, user) {
      if (user) clearInterval(this.timer);
      this.items[this.i].hidden = true;
      this.i = (this.i + step + this.items.length) % this.items.length;
      this.items[this.i].hidden = false;
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }
  customElements.define('rotating-bar', RotatingBar);

  /* --------------------------------------------------- store clock helpers */
  /* Dates are computed in the STORE's time zone (data-tz="+0200"), so a 14:00
     cut-off means 14:00 where the parcels leave, whatever the visitor's clock. */
  const tzOffset = (tz) => {
    const m = /([+-])(\d\d):?(\d\d)/.exec(tz || '');
    return m ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3])) : -new Date().getTimezoneOffset();
  };
  /** "Store-local" date: read it with getUTC* methods. */
  const storeNow = (off) => new Date(Date.now() + off * 60000);
  const isWorkday = (d, weekends) => weekends || (d.getUTCDay() !== 0 && d.getUTCDay() !== 6);
  /** Real instant (ms) of hour h on the store-local day d. */
  const at = (d, h, off) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), h) - off * 60000;

  /* ------------------------------------------------------------- countdown */
  const pad = (n) => String(n).padStart(2, '0');
  class CountDown extends HTMLElement {
    connectedCallback() {
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    end() {
      if (this.dataset.mode === 'daily') {
        /* today's dispatch cut-off; after it (or on days without dispatch) the
           next dispatch day's cut-off when the block asks for it, else none */
        const off = tzOffset(this.dataset.tz);
        const today = storeNow(off);
        const weekends = this.dataset.weekends === 'true';
        const cut = Number(this.dataset.cutoff) || 14;
        if (isWorkday(today, weekends) && Date.now() < at(today, cut, off)) {
          this.phase('today');
          return at(today, cut, off);
        }
        if (this.dataset.next === undefined) return 0;
        for (let i = 1; i <= 7; i++) {
          const d = new Date(today.getTime() + i * 86400000);
          if (!isWorkday(d, weekends)) continue;
          this.phase('next', d, i);
          return at(d, cut, off);
        }
        return 0;
      }
      return Number(this.dataset.end) * 1000;
    }
    phase(name, day, inDays) {
      const wrap = this.closest('[data-countdown-wrap]');
      if (!wrap || wrap.dataset.phase === name + (inDays || '')) return;
      wrap.dataset.phase = name + (inDays || '');
      wrap.setAttribute('data-when-now', name);
      if (!day) return;
      const word = inDays === 1 && this.dataset.tomorrow ? this.dataset.tomorrow : new Intl.DateTimeFormat(document.documentElement.lang || 'en', { weekday: 'long', timeZone: 'UTC' }).format(day);
      wrap.querySelectorAll('[data-day]').forEach((el) => (el.textContent = word));
    }
    tick() {
      const left = this.end() - Date.now();
      if (!(left > 0)) {
        clearInterval(this.timer);
        if (this.dataset.hideWhenDone !== undefined) this.closest('[data-countdown-wrap]')?.setAttribute('hidden', '');
        return;
      }
      this.closest('[data-countdown-wrap]')?.removeAttribute('hidden');
      const s = Math.floor(left / 1000);
      const days = !!this.querySelector('[data-unit="d"]');
      const parts = { d: Math.floor(s / 86400), h: days ? Math.floor((s % 86400) / 3600) : Math.floor(s / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
      this.querySelectorAll('[data-unit]').forEach((el) => {
        const u = el.dataset.unit;
        el.textContent = u === 'd' ? parts.d : pad(parts[u]);
      });
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }
  customElements.define('count-down', CountDown);

  /* -------------------------------------------------------- delivery date */
  class DeliveryDate extends HTMLElement {
    connectedCallback() {
      const off = tzOffset(this.dataset.tz);
      const weekends = this.dataset.weekends === 'true';
      const start = storeNow(off);
      /* after the cut-off (or on a day off) the order counts from the next working day */
      let skip = Number(this.dataset.processing) || 0;
      if (this.dataset.cutoff && (!isWorkday(start, weekends) || Date.now() >= at(start, Number(this.dataset.cutoff), off))) skip += 1;
      const add = (n) => {
        const d = new Date(start);
        let left = n;
        while (left > 0) {
          d.setUTCDate(d.getUTCDate() + 1);
          if (isWorkday(d, weekends)) left--;
        }
        return d;
      };
      const fmt = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
      const min = add(skip + (Number(this.dataset.min) || 3));
      const max = add(skip + (Number(this.dataset.max) || Number(this.dataset.min) || 5));
      this.querySelectorAll('[data-date-min]').forEach((el) => (el.textContent = fmt.format(min)));
      this.querySelectorAll('[data-date-max]').forEach((el) => (el.textContent = fmt.format(max)));
      /* short dates for timelines: today, the dispatch day, the arrival window */
      const short = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
      const ship = skip > 0 ? add(skip) : start;
      this.querySelectorAll('[data-date-today]').forEach((el) => (el.textContent = short.format(start)));
      this.querySelectorAll('[data-date-ship]').forEach((el) => (el.textContent = short.format(ship)));
      this.querySelectorAll('[data-date-range]').forEach((el) => (el.textContent = `${short.format(min)} – ${short.format(max)}`));
    }
  }
  customElements.define('delivery-date', DeliveryDate);

  /* ------------------------------------------------------ predictive search */
  const esc = (t) => String(t ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const sized = (src, w) => (src ? `${src}${src.includes('?') ? '&' : '?'}width=${w}` : '');
  const cents = (price) => Math.round(parseFloat(price) * 100);
  const money = (c) => (window.HALDEN?.formatMoney ? window.HALDEN.formatMoney(c) : (c / 100).toFixed(2));

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-results]');
      let timer;
      this.input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.search(this.input.value.trim()), 220);
      });
      this.closest('dialog')?.addEventListener('dialog:open', () => setTimeout(() => this.input.focus(), 60));
    }
    async search(q) {
      if (!q) {
        this.results.innerHTML = '';
        return;
      }
      this.results.setAttribute('aria-busy', 'true');
      try {
        const params = new URLSearchParams({ q, 'resources[type]': 'product,page', 'resources[limit]': '6', 'resources[options][unavailable_products]': 'last' });
        const data = await fetch(`${this.dataset.url}.json?${params}`).then((r) => r.json());
        if (q !== this.input.value.trim()) return;
        const { products = [], pages = [] } = data.resources?.results || {};
        const d = this.dataset;
        const all = `<a class="button button--outline button--full search-sheet__all" href="${esc(this.querySelector('form').action)}?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last">${esc(d.all.replace('[terms]', q))}</a>`;
        if (!products.length && !pages.length) {
          this.results.innerHTML = `<p class="muted">${esc(d.empty.replace('[terms]', q))}</p>`;
          return;
        }
        let html = '';
        if (products.length) {
          html += `<p class="search-sheet__label">${esc(d.products)}</p>`;
          html += products
            .map((p) => `<a class="search-sheet__item" href="${esc(p.url)}">${p.featured_image?.url ? `<img src="${esc(sized(p.featured_image.url, 160))}" alt="" loading="lazy" width="56" height="56">` : '<span></span>'}<span>${esc(p.title)}</span><strong class="small">${esc(money(cents(p.price)))}</strong></a>`)
            .join('');
        }
        if (pages.length) {
          html += `<p class="search-sheet__label">${esc(d.pages)}</p>`;
          html += pages.map((p) => `<a class="search-sheet__link" href="${esc(p.url)}">${esc(p.title)}</a>`).join('');
        }
        this.results.innerHTML = html + all;
      } catch (e) {
        /* the full search page still works */
      } finally {
        this.results.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* -------------------------------------------------------- recently viewed */
  /* Product pages remember their handle in this browser only (nothing is
     sent anywhere). <recently-viewed> shows them, newest first. */
  const RV_KEY = 'halden:viewed';
  const viewed = () => {
    try {
      return JSON.parse(localStorage.getItem(RV_KEY) || '[]');
    } catch (e) {
      return [];
    }
  };
  document.addEventListener('DOMContentLoaded', () => {
    const handle = document.querySelector('[data-product-handle]')?.dataset.productHandle;
    if (!handle) return;
    try {
      localStorage.setItem(RV_KEY, JSON.stringify([handle, ...viewed().filter((h) => h !== handle)].slice(0, 12)));
    } catch (e) {}
  });
  class RecentlyViewed extends HTMLElement {
    async connectedCallback() {
      const root = (window.HALDEN?.routes?.root || '/').replace(/\/$/, '');
      const handles = viewed().filter((h) => h !== this.dataset.exclude).slice(0, Number(this.dataset.limit) || 4);
      if (!handles.length) return;
      const list = await Promise.all(handles.map((h) => fetch(`${root}/products/${h}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null)));
      const items = list.filter(Boolean);
      if (!items.length) return;
      this.querySelector('[data-grid]').innerHTML = items
        .map(
          (p) => `<li><a class="recent-card" href="${esc(p.url)}"><span class="recent-card__img frame">${p.featured_image ? `<img src="${esc(sized(p.featured_image, 500))}" alt="${esc(p.title)}" loading="lazy">` : ''}</span><span class="recent-card__title">${esc(p.title)}</span><span class="recent-card__price"><strong>${esc(money(p.price))}</strong>${p.compare_at_price > p.price ? ` <s class="muted">${esc(money(p.compare_at_price))}</s>` : ''}</span></a></li>`
        )
        .join('');
      this.hidden = false;
    }
  }
  customElements.define('recently-viewed', RecentlyViewed);

  /* ---------------------------------------------------------- before/after */
  class BeforeAfter extends HTMLElement {
    connectedCallback() {
      const range = this.querySelector('input[type="range"]');
      if (!range) return;
      const set = () => this.style.setProperty('--pos', `${range.value}%`);
      let touched = false;
      range.addEventListener('input', () => {
        touched = true;
        set();
      });
      set();
      /* a short nudge the first time it's seen, so people know it moves */
      if (!motionOn) return;
      const io = new IntersectionObserver(([e]) => {
        if (!e.isIntersecting) return;
        io.disconnect();
        const keys = [50, 32, 66, 50];
        const t0 = performance.now();
        const step = (t) => {
          if (touched) return;
          const k = Math.min(1, (t - t0) / 1600);
          const seg = Math.min(2, Math.floor(k * 3));
          const local = k * 3 - seg;
          const ease = local < 0.5 ? 2 * local * local : 1 - Math.pow(-2 * local + 2, 2) / 2;
          range.value = keys[seg] + (keys[seg + 1] - keys[seg]) * ease;
          set();
          if (k < 1) requestAnimationFrame(step);
        };
        setTimeout(() => requestAnimationFrame(step), 300);
      }, { threshold: 0.6 });
      io.observe(this);
    }
  }
  customElements.define('before-after', BeforeAfter);

  /* --------------------------------------------------------- slider arrows */
  class SliderArrows extends HTMLElement {
    connectedCallback() {
      const track = document.getElementById(this.dataset.for);
      if (!track) return;
      const step = (dir) => {
        const slide = track.firstElementChild;
        const w = slide ? slide.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : track.clientWidth;
        track.scrollBy({ left: dir * w, behavior: reduced ? 'auto' : 'smooth' });
      };
      this.querySelector('[data-prev]')?.addEventListener('click', () => step(-1));
      this.querySelector('[data-next]')?.addEventListener('click', () => step(1));
    }
  }
  customElements.define('slider-arrows', SliderArrows);
})();
